import { useState } from 'react'
import Drawer from '../Drawer'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createProgram, getCycles, getIntelligences, getPrograms, updateProgram } from '../api/endpoints'
import { ApiError } from '../api/client'
import type { Program } from '../api/types'
import { ErrorNotice } from '../components'
import HowItWorks from '../HowItWorks'
import BandsDialog from './BandsDialog'

export default function ProgramsPage() {
  const programs = useQuery({ queryKey: ['programs'], queryFn: getPrograms })
  const intelligences = useQuery({ queryKey: ['intelligences'], queryFn: getIntelligences })
  const [editing, setEditing] = useState<Program | 'new' | null>(null)
  const [bandsFor, setBandsFor] = useState<Program | null>(null)
  const name = (code: string) => intelligences.data?.find((i) => i.code === code)?.name ?? code
  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1>Programs</h1>
        <button type="button" className="primary" onClick={() => setEditing('new')}>New program</button>
      </div>
      <HowItWorks id="programs" title="How programs work" steps={[
        { icon: 'programs', title: 'Create a program', text: 'A course the candidate is applying for. Programs are optional: a campaign can run without asking for one.' },
        { icon: 'check', title: 'Choose what fits', text: 'Pick the intelligences that matter for this program. The questionnaire score is the average of those.' },
        { icon: 'results', title: 'Set admission bands', text: 'Name ranges of composite score, such as “Strong fit”. They are shown to the panel only.' },
        { icon: 'schedule', title: 'Give it a plan', text: 'In a campaign, choose which tests this program takes. Without its own plan a program follows the general plan.' },
      ]} />
      <ErrorNotice error={programs.error} />
      <div className="table-wrap"><table>
        <thead><tr><th>Code</th><th>Program</th><th>Interests that fit</th><th className="num">Questionnaire marks</th><th /></tr></thead>
        <tbody>
          {programs.data?.map((p) => (
            <tr key={p.id}>
              <td><b>{p.code}</b></td><td>{p.name}</td>
              <td style={{ whiteSpace: 'normal', maxWidth: 360 }}>{p.fitIntelligences.length ? p.fitIntelligences.map(name).join(', ') : <span className="muted">none chosen</span>}</td>
              <td className="num">{p.miCountsForMarks ? p.miMaxMarks : <span className="muted">advisory</span>}</td>
              <td><div className="row"><button type="button" onClick={() => setEditing(p)}>Edit</button><button type="button" onClick={() => setBandsFor(p)}>Admission bands</button></div></td>
            </tr>
          ))}
          {programs.data?.length === 0 && <tr><td colSpan={5} className="muted">No programs yet. You can run campaigns without one.</td></tr>}
        </tbody>
      </table></div>
      {editing && <ProgramDialog program={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {bandsFor && <BandsCampaign program={bandsFor} onClose={() => setBandsFor(null)} />}
    </div>
  )
}

/** Bands can apply to a program in every campaign, or be set for one campaign. */
function BandsCampaign({ program, onClose }: { program: Program; onClose: () => void }) {
  const cycles = useQuery({ queryKey: ['cycles'], queryFn: getCycles })
  const [cycleId, setCycleId] = useState<number | null | undefined>(undefined)
  if (cycleId === undefined) {
    return (
      <Drawer title={`Admission bands for ${program.name}`} width={520} onClose={onClose} onSubmit={() => undefined}
        footer={<><button type="button" onClick={onClose}>Cancel</button></>}>
          <label className="field">Where do these bands apply?
            <select onChange={(e) => setCycleId(e.target.value ? Number(e.target.value) : null)} defaultValue="">
              <option value="" disabled>Choose…</option><option value="">Every campaign</option>
              {cycles.data?.map((c) => <option key={c.id} value={c.id}>Only {c.name}</option>)}
            </select>
          </label>
      </Drawer>
    )
  }
  return <BandsDialog title={`Admission bands: ${program.name}`} programId={program.id} cycleId={cycleId} onClose={onClose} />
}

function ProgramDialog({ program, onClose }: { program: Program | null; onClose: () => void }) {
  const queryClient = useQueryClient()
  const intelligences = useQuery({ queryKey: ['intelligences'], queryFn: getIntelligences })
  const [code, setCode] = useState(program?.code ?? '')
  const [name, setName] = useState(program?.name ?? '')
  const [counts, setCounts] = useState(program?.miCountsForMarks ?? true)
  const [max, setMax] = useState(String(program?.miMaxMarks ?? 20))
  const [fit, setFit] = useState<string[]>(program?.fitIntelligences ?? [])
  const save = useMutation({
    mutationFn: () => {
      const body = { code, name, miCountsForMarks: counts, miMaxMarks: Number(max), fitIntelligences: fit }
      return program ? updateProgram(program.id, body) : createProgram(body).then(() => undefined)
    },
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['programs'] }); onClose() },
  })
  const errs = save.error instanceof ApiError ? save.error.fields : {}
  const E = (k: string) => (errs[k] ? <p role="alert" className="muted" style={{ color: 'var(--danger)', margin: '2px 0 8px' }}>{errs[k]}</p> : null)
  return (
    <Drawer title={`${program ? 'Edit program' : 'New program'}`} width={620} onClose={onClose} onSubmit={() => { save.mutate() }}
      footer={<><button type="button" onClick={onClose}>Cancel</button><button type="submit" className="primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save program'}</button></>}>
        <label className="field">Short code<input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="BBA" /></label>{E('code')}
        <label className="field">Name<input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Bachelor of Business Administration" /></label>{E('name')}
        <fieldset className="question-set" style={{ border: 0, padding: 0 }}>
          <legend><b>Interests that fit this program</b></legend>
          <div className="chip-grid">
            {intelligences.data?.map((i) => (
              <label key={i.code} className="check-chip"><input type="checkbox" checked={fit.includes(i.code)} onChange={(e) => setFit(e.target.checked ? [...fit, i.code] : fit.filter((c) => c !== i.code))} /> {i.name}</label>
            ))}
          </div>
        </fieldset>{E('fitIntelligences')}
        <label className="row" style={{ margin: '8px 0' }}><input type="checkbox" checked={counts} onChange={(e) => setCounts(e.target.checked)} /> Add the questionnaire marks to the combined score</label>
        <label className="field">Questionnaire marks out of<input type="number" min={0} max={100} step="0.5" value={max} onChange={(e) => setMax(e.target.value)} style={{ width: 120 }} /></label>{E('miMaxMarks')}
        <ErrorNotice error={Object.keys(errs).length === 0 ? save.error : null} />
    </Drawer>
  )
}
